import { Badge, Collapse } from "react-bootstrap";
import React, { useMemo, useState } from "react";
import {
  faBarcode,
  faBoxOpen,
  faBoxesStacked,
  faChevronDown,
  faChevronUp,
  faClockRotateLeft,
  faDollarSign,
  faExclamationTriangle,
  faGift,
  faLayerGroup,
  faMoneyBillWave,
  faPen,
  faPercent,
  faReceipt,
  faShoppingCart,
  faSitemap,
  faTag,
  faToggleOff,
  faToggleOn,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";

import ActionPillButton from "../../../commons/buttons/ActionPillButton";
import CustomCard from "../../../commons/cards/Card";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

function usageVariant(usageType) {
  switch (usageType) {
    case "SALE":
      return "success";
    case "CONSUMPTION":
      return "warning";
    case "BOTH":
      return "primary";
    default:
      return "secondary";
  }
}

function usageLabel(usageType) {
  switch (usageType) {
    case "SALE":
      return "Venda";
    case "CONSUMPTION":
      return "Consumo";
    case "BOTH":
      return "Venda e Consumo";
    default:
      return "Não informado";
  }
}

function activeLabel(active) {
  return active ? "Ativo" : "Inativo";
}

function activeVariant(active) {
  return active ? "success" : "secondary";
}

function unityLabel(unityType) {
  const labels = {
    UN: "Unidade (UN)",
    ML: "Mililitros (ML)",
    L: "Litros (L)",
    G: "Gramas (g)",
    KG: "Quilogramas (KG)",
    MG: "Miligramas (MG)",
    CL: "Centilitros (CL)",
    DL: "Decilitros (DL)",
    CM3: "Centímetros cúbicos (CM³)",
    M3: "Metros cúbicos (M³)",
    PC: "Peças (PC)",
    DZ: "Dúzia (DZ)",
  };

  return labels[unityType] || unityType || "Não informado";
}

function formatCurrency(value) {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  const numberValue = Number(value);

  if (Number.isNaN(numberValue)) {
    return "-";
  }

  return numberValue.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function formatPercent(value) {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  const numberValue = Number(value);

  if (Number.isNaN(numberValue)) {
    return "-";
  }

  return `${numberValue.toLocaleString("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}%`;
}

function hasValue(value) {
  return value !== null && value !== undefined && value !== "";
}

function PriceRow({ icon, label, value, valueColor }) {
  return (
    <div className="d-flex align-items-center justify-content-between">
      <div
        className="text-muted d-flex align-items-center gap-2"
        style={{ fontSize: 13 }}
      >
        <FontAwesomeIcon icon={icon} />
        <span>{label}</span>
      </div>

      <div
        className="fw-semibold"
        style={{ fontSize: 13, color: valueColor || "inherit" }}
      >
        {value}
      </div>
    </div>
  );
}

function InventoryRow({ icon, label, value, valueColor }) {
  return (
    <div className="d-flex align-items-center justify-content-between">
      <div
        className="text-muted d-flex align-items-center gap-2"
        style={{ fontSize: 13 }}
      >
        <FontAwesomeIcon icon={icon} />
        <span>{label}</span>
      </div>

      <div
        className="fw-semibold"
        style={{ fontSize: 13, color: valueColor || "inherit" }}
      >
        {value}
      </div>
    </div>
  );
}

export default function ProductCard({
  product,
  onEdit,
  onDelete,
  onToggleStatus,
  onManageStock,
  onOpenHistory,
  onSell
}) {
  const [openPricing, setOpenPricing] = useState(false);

  const active = !!product?.active;
  const pricing = product?.pricing;
  const inventory = product?.inventory;

  const currentQuantity = useMemo(() => {
    const value = Number(inventory?.currentQuantity);
    return Number.isNaN(value) ? 0 : value;
  }, [inventory]);

  const minQuantity = useMemo(() => {
    if (!hasValue(inventory?.minQuantity)) return null;
    const value = Number(inventory?.minQuantity);
    return Number.isNaN(value) ? null : value;
  }, [inventory]);

  const lowStock = useMemo(() => {
    if (typeof inventory?.lowStock === "boolean") {
      return inventory.lowStock;
    }

    if (minQuantity === null || minQuantity <= 0) {
      return false;
    }

    return currentQuantity <= minQuantity;
  }, [inventory, currentQuantity, minQuantity]);

  const subtitle = useMemo(() => {
    const parts = [];

    if (product?.category) parts.push(product.category);
    if (product?.brand) parts.push(product.brand);
    if (product?.line) parts.push(product.line);

    return parts.length > 0
      ? parts.join(" • ")
      : product?.description || "Produto cadastrado no sistema";
  }, [product]);

  const hasPricing = useMemo(() => {
    return (
      hasValue(pricing?.costPrice) ||
      hasValue(pricing?.employeeSalePrice) ||
      hasValue(pricing?.customerSalePrice) ||
      hasValue(pricing?.promotionalPrice) ||
      hasValue(pricing?.markupPercentage) ||
      hasValue(pricing?.profitMarginPercentage) ||
      hasValue(pricing?.employeeCommissionValue)
    );
  }, [pricing]);

  const promotionalActive = useMemo(() => {
    if (!hasValue(pricing?.promotionalPrice)) {
      return false;
    }

    const now = new Date();
    const start = pricing?.promotionalStartDate
      ? new Date(pricing.promotionalStartDate)
      : null;
    const end = pricing?.promotionalEndDate
      ? new Date(pricing.promotionalEndDate)
      : null;

    if (start && now < start) return false;
    if (end && now > end) return false;

    return true;
  }, [pricing]);

  return (
    <div style={{ width: "100%" }}>
      <CustomCard>
        <div className="d-flex align-items-start justify-content-between gap-2">
          <div style={{ minWidth: 0 }}>
            <div
              className="fw-semibold"
              style={{ fontSize: 16, lineHeight: "20px" }}
            >
              {product?.name || "Produto"}
            </div>

            <div className="text-muted" style={{ fontSize: 12 }}>
              {subtitle}
            </div>
          </div>

          <div className="d-flex flex-column align-items-end gap-2">
            <Badge bg={activeVariant(active)} pill>
              {activeLabel(active)}
            </Badge>

            <ActionPillButton
              icon={active ? faToggleOff : faToggleOn}
              label={active ? "Inativar" : "Ativar"}
              onClick={onToggleStatus}
              color={active ? "#b42318" : "#198754"}
              borderColor={active ? "#ffd6d6" : "#c9f2d8"}
              title={active ? "Inativar produto" : "Ativar produto"}
            />
          </div>
        </div>

        <hr className="my-3" />

        <div className="d-flex flex-wrap gap-2 mb-3">
          <Badge bg="light" text="dark" pill style={{ fontWeight: 900 }}>
            <FontAwesomeIcon icon={faBoxesStacked} className="me-1" />
            {unityLabel(product?.unityType)}
          </Badge>

          <Badge bg={usageVariant(product?.usageType)} pill style={{ fontWeight: 900 }}>
            <FontAwesomeIcon icon={faBoxOpen} className="me-1" />
            {usageLabel(product?.usageType)}
          </Badge>

          {product?.category ? (
            <Badge bg="light" text="dark" pill style={{ fontWeight: 900 }}>
              <FontAwesomeIcon icon={faTag} className="me-1" />
              {product.category}
            </Badge>
          ) : null}

          {product?.brand ? (
            <Badge bg="light" text="dark" pill style={{ fontWeight: 900 }}>
              <FontAwesomeIcon icon={faSitemap} className="me-1" />
              {product.brand}
            </Badge>
          ) : null}

          {promotionalActive ? (
            <Badge bg="danger" pill style={{ fontWeight: 900 }}>
              <FontAwesomeIcon icon={faGift} className="me-1" />
              Promoção ativa
            </Badge>
          ) : null}

          {lowStock ? (
            <Badge bg="warning" text="dark" pill style={{ fontWeight: 900 }}>
              <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
              Estoque baixo
            </Badge>
          ) : null}
        </div>

        <div className="d-flex flex-column gap-2">
          <div className="d-flex align-items-center justify-content-between">
            <div
              className="text-muted d-flex align-items-center gap-2"
              style={{ fontSize: 13 }}
            >
              <FontAwesomeIcon icon={faLayerGroup} />
              <span>Código interno</span>
            </div>
            <div className="fw-semibold" style={{ fontSize: 13 }}>
              {product?.internalCode || "-"}
            </div>
          </div>

          <div className="d-flex align-items-center justify-content-between">
            <div
              className="text-muted d-flex align-items-center gap-2"
              style={{ fontSize: 13 }}
            >
              <FontAwesomeIcon icon={faBarcode} />
              <span>Código de barras</span>
            </div>
            <div className="fw-semibold" style={{ fontSize: 13 }}>
              {product?.barCode || "-"}
            </div>
          </div>

          <InventoryRow
            icon={faBoxesStacked}
            label="Quantidade em estoque"
            value={currentQuantity}
            valueColor={lowStock ? "#b54708" : "#0891b2"}
          />

          <InventoryRow
            icon={faBoxesStacked}
            label="Estoque mínimo"
            value={minQuantity ?? "-"}
            valueColor={lowStock ? "#b54708" : "inherit"}
          />
        </div>

        <hr className="my-3" />

        <div>
          <button
            type="button"
            onClick={() => setOpenPricing((prev) => !prev)}
            className="w-100 d-flex align-items-center justify-content-between border-0 bg-transparent p-0"
            aria-expanded={openPricing}
            aria-controls={`product-pricing-${product?.id || "item"}`}
            style={{ cursor: "pointer" }}
          >
            <div
              className="fw-semibold d-flex align-items-center gap-2"
              style={{ fontSize: 14 }}
            >
              <FontAwesomeIcon icon={faDollarSign} />
              <span>Preços</span>
            </div>

            <div className="d-flex align-items-center gap-2">
              {hasValue(pricing?.customerSalePrice) ? (
                <span className="fw-semibold" style={{ fontSize: 13, color: "#198754" }}>
                  {formatCurrency(pricing?.customerSalePrice)}
                </span>
              ) : (
                <span className="text-muted" style={{ fontSize: 13 }}>
                  Sem preço
                </span>
              )}

              <FontAwesomeIcon
                icon={openPricing ? faChevronUp : faChevronDown}
                className="text-muted"
              />
            </div>
          </button>

          <Collapse in={openPricing}>
            <div id={`product-pricing-${product?.id || "item"}`} className="pt-3">
              {hasPricing ? (
                <div className="d-flex flex-column gap-2">
                  <PriceRow
                    icon={faMoneyBillWave}
                    label="Custo"
                    value={formatCurrency(pricing?.costPrice)}
                  />

                  <PriceRow
                    icon={faReceipt}
                    label="Venda ao cliente"
                    value={formatCurrency(pricing?.customerSalePrice)}
                    valueColor="#198754"
                  />

                  <PriceRow
                    icon={faReceipt}
                    label="Venda ao profissional"
                    value={formatCurrency(pricing?.employeeSalePrice)}
                  />

                  <PriceRow
                    icon={faGift}
                    label="Preço promocional"
                    value={formatCurrency(pricing?.promotionalPrice)}
                    valueColor={promotionalActive ? "#dc3545" : "inherit"}
                  />

                  <PriceRow
                    icon={faPercent}
                    label="Markup"
                    value={formatPercent(pricing?.markupPercentage)}
                  />

                  <PriceRow
                    icon={faPercent}
                    label="Margem de lucro"
                    value={formatPercent(pricing?.profitMarginPercentage)}
                  />

                  <PriceRow
                    icon={faPercent}
                    label="Comissão"
                    value={
                      pricing?.employeeCommissionType && hasValue(pricing?.employeeCommissionValue)
                        ? pricing.employeeCommissionType === "PERCENT"
                          ? formatPercent(pricing.employeeCommissionValue)
                          : formatCurrency(pricing.employeeCommissionValue)
                        : "-"
                    }
                  />
                </div>
              ) : (
                <div className="text-muted" style={{ fontSize: 13 }}>
                  Nenhum preço cadastrado.
                </div>
              )}
            </div>
          </Collapse>
        </div>

        <div className="d-flex gap-2 flex-wrap mt-3">
          <ActionPillButton
            icon={faPen}
            label="Editar"
            onClick={onEdit}
            color="#2f2a7a"
            borderColor="#dfe3ff"
            title="Editar produto"
          />

          <ActionPillButton
            icon={faTrash}
            label="Excluir"
            onClick={onDelete}
            color="#b42318"
            borderColor="#ffd6d6"
            title="Excluir produto"
          />

          {onManageStock ? (
            <ActionPillButton
              icon={faBoxesStacked}
              label="Estoque"
              onClick={onManageStock}
              color="#0891b2"
              borderColor="#cffafe"
              title="Gerenciar estoque"
            />


          ) : null}

          <ActionPillButton
            icon={faClockRotateLeft}
            label="Histórico"
            onClick={onOpenHistory}
            color="#7c3aed"
            borderColor="#e9d5ff"
          />

          <ActionPillButton
            icon={faShoppingCart}
            label="Vender"
            onClick={onSell}
            color="#16a34a"
            borderColor="#dcfce7"
            title="Registrar venda"
          />
        </div>
      </CustomCard>
    </div>
  );
}